import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Subject, catchError, filter, takeUntil, throwError } from 'rxjs';

import { UserService } from 'app/core/user/user.service';
import { Settings } from 'app/modules/_common/settings';
import { TeamPeopleService } from 'app/modules/teams/people/people.service';
import { WorkDayType } from 'app/modules/teams/people/people.type';
import { WorkdayPlanService } from './workday-plan.service';

@Component({
    selector: 'workday-plan',
    standalone: true,
    imports: [CommonModule, MatIconModule],
    templateUrl: './workday-plan.component.html'
})
export class WorkdayPlanComponent implements OnInit, OnChanges, OnDestroy {

    currentUser = this._userService.user;

    others = [];
    contact = {
        'workdays': null as WorkDayType,
        'profileblurb': '',
        'profilePicture': null,
        'background': '',
    };
    contact_visible = {};
    contact_protocolhandler = {};

    today: number;
    _isLoading: boolean = false;
    weekDayKeys = ['S', 'M', 'Tu', 'W', 'Th', 'F', 'Sa'];

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    @Input() isReadOnly: boolean = true;
    @Input() colorScheme: 'default' | 'light' = 'light';
    @Input() personId: string;
    @Input() showLeftBorder: boolean = false;
    @Input() isCursorPointer: boolean = false;

    @Output() workDayUpdated = new EventEmitter<void>();

    constructor(
        private _service: TeamPeopleService,
        private _settings: Settings,
        private _changeDetectorRef: ChangeDetectorRef,
        private _userService: UserService,
        private _workdayPlanService: WorkdayPlanService,
    ){
        this.today = new Date().getDay();
    }

    ngOnInit(): void
    {
        this._settings.getSettingsPeople()
            .pipe(takeUntil(this._unsubscribeAll), filter((raw) => raw != null))
            .subscribe((data) =>
            {
                data.items.forEach(({ name, value }) => {
                    name = name.toLowerCase();

                    if (name == "visiblepersonfields") {
                        this.contact_visible = JSON.parse(value);
                    }
                    if (name == "fieldprotocolhandlers") {
                        this.contact_protocolhandler = JSON.parse(value);
                    }
                });

                this._changeDetectorRef.markForCheck();
            });

        setTimeout(()=> this.reload());
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (changes['personId'] && !changes['personId'].firstChange) {
            const newPersonId = changes['personId'].currentValue;
            if (newPersonId) {
                this.personId = newPersonId;
                this.getPersonDetails(this.personId);
            }
        }
    }

    ngOnDestroy(): void
    {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    public formatScheduleDay(value: number | null | undefined): string {
        if (value == null) return "N";
        if (value == -1) return "-";
        if (value == 0) return "N";
        if (value == 1) return "O";
        if (value == 2) return "R";
        return "-";
    }

    /**
     * Get CSS class for button based on work day type
     */
    getButtonBorderClass(dayKey: string, week: 'this' | 'next' = 'this', index: number): string {
        const workDayValue = week === 'this'
            ? this.formatScheduleDay(this.getThisWeekDay(dayKey))
            : this.formatScheduleDay(this.getNextWeekDay(dayKey));

        const isWeekend = index === 0 || index === 6; // Sunday (0) or Saturday (6)
        const isWeekday = index >= 1 && index <= 5; // Monday through Friday

        // If it's a non-working day (N), use light gray border
        if (workDayValue === 'N') {
            return 'border-[#a5a5a5]';
        }

        // For light colorScheme
        if (this.colorScheme === 'light') {
            if (isWeekend && workDayValue === 'N') {
                return 'border-[#4b4b50]'; // Gray border for weekends
            }

            if (isWeekday || workDayValue !== 'N') {
                return 'border-[#fff]'; // White border for weekdays
            }
        }

        // For default colorScheme
        if (this.colorScheme === 'default') {
            return 'border-[#000e70]'; // Blue border for default scheme
        }

        // Fallback
        return 'border-[#000e70]';
    }

    get textColorClass(): string {
        return this.colorScheme === 'default' ? 'text-[#3f3f46]' : 'text-[#fff]';
    }

    get labelTextColorClass(): string {
        return this.colorScheme === 'default' ? 'text-[#2b2b2b]' : 'text-[#fff]';
    }

    get arrowIconColor(): string {
        return this.colorScheme === 'default' ? '#000e70' : '#fff';
    }

    /**
     * Get combined button classes as an object for ngClass
     */
    getButtonClasses(dayKey: string, week: 'this' | 'next', index: number): { [key: string]: boolean } {
        const classes: { [key: string]: boolean } = {};

        // Add text color class
        if (this.textColorClass) {
            classes[this.textColorClass] = true;
        }

        // Add border class
        const borderClass = this.getButtonBorderClass(dayKey, week, index);
        if (borderClass) {
            classes[borderClass] = true;
        }

        // Add cursor classes
        classes['cursor-pointer'] = this.isCursorPointer;
        classes['cursor-default'] = !this.isCursorPointer;

        return classes;
    }

    /**
     * Handle work day selection
     */
    selectWorkDay(week: 'this' | 'next', day: string, dayIndex: number): void {

        if(this.isReadOnly) {
            return;
        }

        // Toggle between N (0), O (1), R (2)
        const currentValue = week === 'this' ? this.getThisWeekDay(day) : this.getNextWeekDay(day);
        const newValue = (currentValue + 1) % 3;

        if (this.contact?.workdays) {
            const dayMap: { [key: string]: string } = {
                'S': week === 'this' ? 'SundayTW' : 'SundayNW',
                'M': week === 'this' ? 'MondayTW' : 'MondayNW',
                'Tu': week === 'this' ? 'TuesdayTW' : 'TuesdayNW',
                'W': week === 'this' ? 'WednesdayTW' : 'WednesdayNW',
                'Th': week === 'this' ? 'ThursdayTW' : 'ThursdayNW',
                'F': week === 'this' ? 'FridayTW' : 'FridayNW',
                'Sa': week === 'this' ? 'SaturdayTW' : 'SaturdayNW'
            };

            this.contact.workdays[dayMap[day]] = newValue;
            this.updateWorkDayPlan(dayMap[day], newValue);
        }
    }

    /**
     * Get work day value for this week
     */
    getThisWeekDay(day: string): number {
        if (!this.contact?.workdays) return -1;

        const dayMap: { [key: string]: string } = {
            'S': 'SundayTW',
            'M': 'MondayTW',
            'Tu': 'TuesdayTW',
            'W': 'WednesdayTW',
            'Th': 'ThursdayTW',
            'F': 'FridayTW',
            'Sa': 'SaturdayTW'
        };

        return this.contact.workdays[dayMap[day]] || 0;
    }

    /**
     * Get work day value for next week
     */
    getNextWeekDay(day: string): number {
        if (!this.contact?.workdays) return -1;

        const dayMap: { [key: string]: string } = {
            'S': 'SundayNW',
            'M': 'MondayNW',
            'Tu': 'TuesdayNW',
            'W': 'WednesdayNW',
            'Th': 'ThursdayNW',
            'F': 'FridayNW',
            'Sa': 'SaturdayNW'
        };

        return this.contact.workdays[dayMap[day]] || 0;
    }

    /**
     * Update work day plan via API
     */
    private updateWorkDayPlan(dayField: string, newValue: number): void {
        if (!this.personId) {
            console.error('Person ID is required to update work day plan');
            return;
        }

        this._isLoading = true;

        const updatePayload = {
            WorkDays: {
                SundayTW: this.contact.workdays?.SundayTW || 0,
                MondayTW: this.contact.workdays?.MondayTW || 0,
                TuesdayTW: this.contact.workdays?.TuesdayTW || 0,
                WednesdayTW: this.contact.workdays?.WednesdayTW || 0,
                ThursdayTW: this.contact.workdays?.ThursdayTW || 0,
                FridayTW: this.contact.workdays?.FridayTW || 0,
                SaturdayTW: this.contact.workdays?.SaturdayTW || 0,
                SundayNW: this.contact.workdays?.SundayNW || 0,
                MondayNW: this.contact.workdays?.MondayNW || 0,
                TuesdayNW: this.contact.workdays?.TuesdayNW || 0,
                WednesdayNW: this.contact.workdays?.WednesdayNW || 0,
                ThursdayNW: this.contact.workdays?.ThursdayNW || 0,
                FridayNW: this.contact.workdays?.FridayNW || 0,
                SaturdayNW: this.contact.workdays?.SaturdayNW || 0
            }
        };

        this._service.updatePersonWorkDays(this.personId, updatePayload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                catchError((error) => {
                    console.error('Error updating work day plan:', error);
                    this._isLoading = false;

                    if (this.contact?.workdays) {
                        const currentValue = this.contact.workdays[dayField];
                        this.contact.workdays[dayField] = (currentValue - 1 + 3) % 3;
                    }

                    return throwError(() => error);
                })
            )
            .subscribe({
                next: (response) => {
                    this.workDayUpdated.emit();
                    // Notify via WorkdayPlanService for cross-component communication (e.g., to update main.component workday-plan)
                    this._workdayPlanService.notifyWorkDayUpdated();
                    this.getPersonDetails(this.personId);
                    this._isLoading = false;
                    this._changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    console.error('Failed to update work day plan:', error);
                    this._isLoading = false;
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    getPersonDetails(_personId: string) {
        this._isLoading = true;
        this._service.getPerson(this.currentUser.displayName || '', _personId)
            .pipe(takeUntil(this._unsubscribeAll), filter((raw) => raw != null))
            .subscribe((data) =>
            {
                this.contact.profilePicture = null;

                for (const name in this.contact) {
                    switch (true) {
                        case name == 'workdays':
                            this.contact[name] = {} as WorkDayType
                            break;
                        default:
                            this.contact[name] = '';
                    }
                }

                //p.isHidden == false do not filter isHidden because WorkDays is hidden
                var main_list = data.filter(p => p.fieldTypeId != -6 && p.value != null);

                main_list.forEach(({ fieldTypeId, name, value, dynamicId }) => {
                    name = name.toLowerCase();

                    if (this.contact[name] !== undefined) {

                        if (name == "photo") {
                            this._service.getPersonPhoto(dynamicId)
                                .pipe(takeUntil(this._unsubscribeAll))
                                .subscribe((base64) => {
                                    this.contact.profilePicture = base64 ? base64 : null;

                                    this._changeDetectorRef.markForCheck();
                                });
                        }else
                        if (name == "workdays") {
                            try {
                                this.contact[name] = JSON.parse(value);
                            }
                            catch {}
                        }
                        else {
                            this.contact[name] = value;
                        }
                    } else {
                        this.others.push({ fieldTypeId: fieldTypeId, name: name, value: value });
                    }
                });

                this._isLoading = false;
                this._changeDetectorRef.markForCheck();
            });

    }

    /**
     * Public method to reload work day data
     * Can be called from parent component using ViewChild
     */
    public reload(personId?: any): void {
        if (personId) {
            this.getPersonDetails(personId);
        } else if (this.personId) {
            this.getPersonDetails(this.personId);
        }
    }
}
